import { Component, computed, effect, inject, TemplateRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { updateVisitHistoryData } from '../../../../../store/global/global.actions';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ServiceDetailPageStore } from './service-detail-page.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { LayoutService } from '../../../../layout/services/layout.service';
import { selectService } from '../../../store/service/service.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { ServiceDetailPageStore2 } from './service-detail-page.store2';
import { ServiceTypeEnum, serviceTypeNameAllowed } from '@libs/types/src/services/enum/service-type.enum';
import { serviceUri } from 'packages/client/src/app/utils/router.utils';
import { Ripple } from '@openng/optimus-ui/ripple';
import { AsyncPipe, NgClass } from '@angular/common';
import { featureOn } from '../../../../../feature-flag';
import { SERVICE_SETTINGS_DIET_TYPE_KEY } from '@libs/types/src/services/types/common-service-settings.type';

@Component({
    selector: 'app-service-detail-page',
    templateUrl: './service-detail-page.component.html',
    styleUrls: ['./service-detail-page.component.scss'],
    providers: [ServiceDetailPageStore, ServiceDetailPageStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RouterOutlet,
        Ripple,
        NgClass,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class ServiceDetailPageComponent extends SubscriptionComponent {

    public readonly store2 = inject(ServiceDetailPageStore2);
    protected readonly store = inject(ServiceDetailPageStore);
    private readonly router = inject(Router);
    private readonly layoutService = inject(LayoutService);
    private readonly translate = inject(TranslateService);
    private readonly breadcrumbs = inject(BreadcrumbService);


    protected readonly submenuContent = viewChild<TemplateRef<unknown>>('submenuContent');

    protected routerEvent = toSignal(this.router.events);
    protected service = this.store.globalStore!.selectSignal(selectService);

    protected menuItems = computed(() => {
        const serviceUpdate = this.store2.isAllowed('service.update')();
        const administrationRead = this.store2.isAllowed('administration.read')();

        const items = administrationRead ? ['basic-information'] : [];
        const isHeathCare = this.service()?.type === ServiceTypeEnum.HEALTH_CARE;
        const isSocial = this.service()?.type === ServiceTypeEnum.SOCIAL;
        const expertiseCode = this.service()?.expertise?.code || '';
        const useDiet = JSON.parse(this.service()?.serviceSettings?.find((it) => it.key === SERVICE_SETTINGS_DIET_TYPE_KEY)?.data || '{}')?.useCard ?? true;
        const useAreas = serviceTypeNameAllowed.socialServiceArea.includes(this.service()?.serviceType?.name || '');

        if (serviceUpdate) {
            items.push('settings');
            items.push('groups');

            isHeathCare && items.push(
                'procedure',
                'payments',
            );
            if (isSocial || isHeathCare) {
                items.push('patterns');
            }
            if (isSocial) {
                if (useAreas) {
                    items.push('social-areas');
                }
                items.push('offers');
                // if (featureOn.SOCIAL_JANUARY && useDiet && this.store2.isAllowed('serviceDiet.update')()) {
                    // items.push('diet');
                // }
                if (featureOn.WAREHOUSE_MATERIAL && this.store2.isAllowed('warehouseMaterial.update')()) items.push('warehouse-materials');

            }
            if (['902', '913', '925'].includes(expertiseCode)) {
                items.push('nursing-diagnoses');
            }
            items.push('logs');
        }

        if (isSocial && featureOn.SOCIAL_PROJECTS && this.store2.isAllowed('socialProject.read')()) {
            items.push('projects');
        }
        if (featureOn.ARCHIVE_DISPOSAL && this.store2.isAllowed('archiveDisposal.read')()) {
            items.push('archive-disposal');
        }

        return items;
    });

    protected serviceId = 0;

    constructor() {
        super();
        this.store.onRouteChange(
            'getServiceDetail',
            { id: 'serviceId' },
            { asFilter: true },
        );
        this.store.doOnActions(
            () =>
                this.store.api.getServiceDetail(
                    FilterBuilder.filter({ id: this.serviceId }),
                ),
            'patchService',
            'updateServiceSettings',
        );

        this.subs.push(
            this.store.data.serviceDetail$.subscribe((services) => {
                const service = services?.['items']?.[0];
                if (service) {
                    this.serviceId = Number(service?.id || 0);
                    this.breadcrumbs.set(
                        `@${BreadcrumbsEnum.Service}`,
                        `${BreadcrumbsEnum.Service}|${service?.name}`,
                    );
                    this.store.globalStore!.dispatch(updateVisitHistoryData());
                } else {
                    this.breadcrumbs.set(
                        `@${BreadcrumbsEnum.Service}`,
                        `${BreadcrumbsEnum.Service}|${this.translate.instant('service.create')}`,
                    );
                }
            }),
        );

        effect(() => {
            const event = this.routerEvent();
            const menu = this.menuItems();
            if (!menu.length) return;

            if (event instanceof NavigationEnd) {
                // content/services/16
                if (event.url.split('/').length <= 4) {
                    this.router.navigateByUrl(`${event.url}/${menu[0]}`);
                }
            }
        });

        effect((onCleanup) => {
            this.layoutService.setSubmenuContent(this.submenuContent() ?? null);

            onCleanup(() => {
                this.layoutService.setSubmenuContent(null);
                this.layoutService.setSubsubmenuContent(null);
            });
        });
    }

    public async itemOnClick(path: string) {
        if (this.serviceId) {
            await this.router.navigateByUrl(serviceUri(this.serviceId, path));
        }
    }
}
